import EmailRounded from '@mui/icons-material/EmailRounded';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { Box, Stack, Typography } from '@mui/material';
import { DataTable, type Column } from '../../components/DataTable';
import { ErrorState, LoadingRows } from '../../components/states';
import { StatusChip } from '../../components/StatusChip';
import { Panel } from '../../components/ui';
import { dateTime } from '../../lib/format';
import { htmlToPlain } from '../../lib/richText';
import { useTenantMessages, type PlatformMessage } from './api';
import { WHATSAPP_GREEN } from './SendMessageDialog';

/** Tenant → Messages: WhatsApp messages and emails PackBytes sent this shop (send from the header). */
export function TenantMessagesTab({ tenant }: { tenant: { id: string; name: string } }) {
  const q = useTenantMessages(tenant.id);
  const columns: Column<PlatformMessage>[] = [
    { key: 'when', header: 'When', render: (m) => dateTime(m.createdAt) },
    {
      key: 'channel',
      header: 'By',
      render: (m) =>
        m.channel === 'WHATSAPP' ? (
          <WhatsAppIcon fontSize="small" sx={{ color: WHATSAPP_GREEN }} aria-label="WhatsApp" />
        ) : (
          <EmailRounded fontSize="small" aria-label="Email" />
        ),
    },
    {
      key: 'to',
      header: 'To',
      render: (m) => (m.channel === 'WHATSAPP' ? `+${m.recipient}` : m.recipient),
    },
    {
      key: 'message',
      header: 'Message',
      render: (m) => (
        <Box sx={{ minWidth: 0 }}>
          {m.subject && (
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {m.subject}
            </Typography>
          )}
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {m.channel === 'EMAIL' ? htmlToPlain(m.body) : m.body}
          </Typography>
          {m.error && (
            <Typography variant="caption" color="error">
              {m.error}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (m) => (
        <StatusChip status={m.status} label={m.status === 'SENT' ? 'Sent' : 'Not sent'} />
      ),
    },
    { key: 'by', header: 'Sent by', render: (m) => m.sentBy.name, hideBelow: 'md' },
  ];
  return (
    <Stack spacing={2.5}>
      <Panel
        title="Messages from PackBytes"
        subtitle="WhatsApp messages and emails sent to this shop from PackBytes’ own number and mail server."
        padded={false}
      >
        {q.isPending ? (
          <LoadingRows rows={3} />
        ) : q.isError ? (
          <ErrorState error={q.error} onRetry={() => void q.refetch()} />
        ) : (
          <DataTable
            columns={columns}
            rows={q.data.data}
            rowKey={(m) => m.id}
            empty={<Box sx={{ p: 3, color: 'text.secondary' }}>No messages sent yet.</Box>}
          />
        )}
      </Panel>
    </Stack>
  );
}
